import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import { ErrorList, InputTableColumns } from '../../../../components/InputTable/InputTable.type';
import {
  EligibilityThresholds,
  QualificationThresholds,
} from './QualificationThreshold.types';
import { SessionsParametersLimitCell } from '../SessionsParametersCardLimit/SessionsParametersLimitCell/SessionsParametersLimitCell.component';
import { useLayoutContext } from '../../../../Context/Layout.context';
import _ from 'lodash';
import { saveQualificationThresholds } from '../../../../services/exams/examSheet/examSheet.service';
import { AxiosResponse } from 'axios';
import { getChangesFromArrayOfObject } from '../../../../utils/getChangesFromArrayOfObject';
import { OverflowTypograpy } from '../../../../components/OverflowTypography/OverflowTypography.component';
import { InactiveProcessBadge } from '../InactiveProcessBadge/InactiveProcessBadge.component';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import { useTranslation } from 'react-i18next';

interface QualificationThresholdProps {
  apiResponse: string;
  items: QualificationThresholds[];
  onClose: () => void;
  reload: () => void;
  isLoading: boolean;
  isExaminerView: boolean;
  onChange?: (isDataChanged: boolean) => void;
}

export const QualificationThreshold: React.FC<QualificationThresholdProps> = ({
  apiResponse,
  items,
  onClose,
  reload,
  isLoading,
  isExaminerView,
  onChange,
}) => {
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const { openAlert } = useLayoutContext();
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [parameters, setParameters] = useState<QualificationThresholds[]>(items);

  const { t } = useTranslation();

  const QualificationThresholdColumns: InputTableColumns[] = [
    {
      id: 'examTaskGroupName',
      label: t('sessionsParameters:qualificationThreshold.table.examTaskGroupName'),
    },
    {
      id: 'manageSpacing',
      label: '',
    },
    {
      id: 'examinersRequiredExamTaskGroupSolutionScoresWithinTolerance',
      label: t(
        'sessionsParameters:qualificationThreshold.table.examinersRequiredExamTaskGroupSolutionScoresWithinTolerance',
      ),
    },
    {
      id: 'examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance',
      label: t(
        'sessionsParameters:qualificationThreshold.table.examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance',
      ),
    },
  ];

  const handleValueCallback = useCallback(
    (key: keyof QualificationThresholds, value: number | null, id: string | number) => {
      setParameters((state: QualificationThresholds[]) => {
        return state.map((s: QualificationThresholds) => {
          if (s.examTaskGroupId === id) {
            s[key] = value as never;
          }

          return s;
        });
      });
    },
    [parameters, setParameters],
  );

  const getValidationMessage = (error: ErrorList): string => {
    const errorMessage = `Podana wartość jest nieprawidłowa, prawidłowa wartość powinna mieścić się w zakresie od 0 do ${error.value}.`;
    return errorMessage;
  };

  const validate = (limits: QualificationThresholds): ErrorList[] => {
    const errorList: ErrorList[] = [];
    const {
      examTaskGroupId,
      examinersRequiredExamTaskGroupSolutionScoresWithinTolerance,
      examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance,
      examiningTeamChairmanQualificationRequiredSolutionsCount,
      examingTeamMemberQualitificationRequiredSolutionsCount,
    } = limits;

    if (
      examinersRequiredExamTaskGroupSolutionScoresWithinTolerance &&
      examingTeamMemberQualitificationRequiredSolutionsCount &&
      examinersRequiredExamTaskGroupSolutionScoresWithinTolerance >
        examingTeamMemberQualitificationRequiredSolutionsCount
    ) {
      errorList.push({
        type: 'examinersRequiredExamTaskGroupSolutionScoresWithinTolerance',
        id: examTaskGroupId,
        value: examingTeamMemberQualitificationRequiredSolutionsCount,
      });
    }

    if (
      examiningTeamChairmanQualificationRequiredSolutionsCount &&
      examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance &&
      examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance >
        examiningTeamChairmanQualificationRequiredSolutionsCount
    ) {
      errorList.push({
        type: 'examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance',
        id: examTaskGroupId,
        value: examiningTeamChairmanQualificationRequiredSolutionsCount,
      });
    }

    return errorList;
  };

  const valueValidation = (
    type: keyof QualificationThresholds,
    value: number | null,
    limits: QualificationThresholds,
  ): boolean => {
    const isValid = validate({ ...limits, [type]: value });
    if (!isValid.length) {
      setInvalidType((prev) => prev.filter((e) => e.id !== limits.examTaskGroupId));
      return isValid.length > 0;
    } else {
      setInvalidType((prev) => [...prev, ...isValid]);
      return isValid.length > 0;
    }
  };

  const getAriaLabel = (type: string, examTaskGroupName: string) => {
    switch (type) {
      case 'examinersRequiredExamTaskGroupSolutionScoresWithinTolerance':
        return `Liczba RGZ w zakresie tolerancji dla E w grupie zadań ${examTaskGroupName}`;
      case 'examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance':
        return `Liczba RGZ w zakresie tolerancji dla PZE w grupie zadań ${examTaskGroupName}`;
    }
  };

  const getRowComponent = (
    type: string,
    value: QualificationThresholds,
    isValid: boolean,
  ): React.ReactNode | string | null => {
    if (value.isProcessActive) {
      switch (type) {
        case 'examTaskGroupName':
          return (
            <OverflowTypograpy
              style={{
                fontWeight: 'bolder',
                paddingTop: 2,
                fontSize: '1rem',
                color: '#3C419A',
                width: 200,
              }}
            >
              {value[type]}
            </OverflowTypograpy>
          );
        case 'examinersRequiredExamTaskGroupSolutionScoresWithinTolerance':
        case 'examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance':
          const isActive =
            type === 'examinersRequiredExamTaskGroupSolutionScoresWithinTolerance'
              ? Boolean(value.examingTeamMemberQualitificationRequiredSolutionsCount)
              : Boolean(value.examiningTeamChairmanQualificationRequiredSolutionsCount);
          return (
            <SessionsParametersLimitCell<QualificationThresholds>
              type={type}
              value={value}
              initialValue={value[type]}
              handleValueChange={handleValueCallback}
              validation={valueValidation}
              isValid={isValid}
              isNotVisible={!isActive}
              idKey={'examTaskGroupId'}
              noSwitchMode={true}
              ariaLabel={getAriaLabel(type, value.examTaskGroupName)}
            />
          );
        case 'manageSpacing':
          return <div style={{ width: 350 }} />;
        default:
          return null;
      }
    } else {
      switch (type) {
        case 'examTaskGroupName':
          return (
            <OverflowTypograpy
              style={{
                fontWeight: 'bolder',
                paddingTop: 2,
                fontSize: '1rem',
                color: '#3C419A',
                width: 200,
              }}
            >
              {value[type]}
            </OverflowTypograpy>
          );
        case 'manageSpacing':
          return <InactiveProcessBadge processType={AssessmentSessionProcessTypeId.Qualification} />;
        default:
          return null;
      }
    }
  };

  const handleDiscardCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    setIsEdited(apiResponse ? !_.isEqual(JSON.parse(apiResponse), parameters) : false);
  }, [parameters, apiResponse]);

  useEffect(() => {
    onChange && onChange(isEdited);
  }, [isEdited]);

  const handleSaveCallback = useCallback(() => {
    const apiData: QualificationThresholds[] = JSON.parse(apiResponse);
    const filterData: QualificationThresholds[] = getChangesFromArrayOfObject<QualificationThresholds>(
      apiData,
      parameters,
    );
    const data: EligibilityThresholds = {
      eligibilityThresholds: filterData.map((p) => {
        return {
          examTaskGroupId: p.examTaskGroupId,
          examinersRequiredExamTaskGroupSolutionScoresWithinTolerance:
            p.examinersRequiredExamTaskGroupSolutionScoresWithinTolerance,
          examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance:
            p.examiningTeamChairmansRequiredExamTaskGroupSolutionScoresWithinTolerance,
        };
      }),
    };
    saveQualificationThresholds(data, isExaminerView)
      .then((res: AxiosResponse<boolean>) => {
        if (res.data) {
          openAlert('success');
          reload();
        } else {
          openAlert('error');
        }
      })
      .catch(() => {
        openAlert('error');
      });
  }, [parameters, onClose]);

  return (
    <InputTable<QualificationThresholds>
      columns={QualificationThresholdColumns}
      emptyMessage={t('toleranceRange:message.empty')}
      parameters={parameters}
      items={items}
      keyParam={'examTaskGroupId'}
      invalidType={invalidType}
      headerText={t('sessionsParameters:qualificationToleranceRange.header')}
      onCancel={handleDiscardCallback}
      onSave={handleSaveCallback}
      getValidationMessage={getValidationMessage}
      getRowComponent={getRowComponent}
      edited={isEdited}
      isLoading={isLoading}
      activeProcessRow={'isProcessActive'}
    />
  );
};
